import { Component, inject, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatMenuModule } from '@angular/material/menu';
import { Router, RouterLink, RouterOutlet } from '@angular/router';
import { PwaInstallComponent } from '@library/components/app-pwa-install/app-pwa-install.component';
import { ThemeToggleComponent } from '@library/components/app-theme-toggle/app-theme-toggle.component';
import { FuseLoadingBarComponent } from '@library/components/loading-bar';
import { FuseNavigationService, FuseVerticalNavigationComponent } from '@library/components/navigation';
import { UserComponent } from '@library/components/user/user.component';
import { FuseMediaWatcherService } from '@library/services/media-watcher';
import { AuthService } from 'app/core/auth/auth.service';
import { NavigationService } from 'app/core/navigation/navigation.service';
import { Navigation } from 'app/core/navigation/navigation.types';
import { LOCAL_STORAGE_KEYS } from 'app/models/localStorageKeys.enum';
import { DateUtilsService } from 'app/pages/time-tracking/services/date.utils.service';
import { AppVersionService } from 'app/services/app-version.service';
import { canUserTrackTimeInEnterprise } from 'app/core/auth/timerecord-role.util';
import { landingUrlFor } from 'app/core/auth/guards/role.guard';
import { EnterpriseFeatureConfigService } from 'app/services/enterprise-feature-config.service';
import { EnterpriseService } from 'app/services/enterprise.service';
import { SecurityProfileService } from 'app/services/securityProfile.service';
import { ShiftsService } from 'app/services/shift.service';
import { UserService } from 'app/services/user.service';
import { Subject, takeUntil } from 'rxjs';
import { Enterprise, ROLES } from 'shared-models';

@Component({
  selector: 'compact-layout',
  templateUrl: './compact.component.html',
  encapsulation: ViewEncapsulation.None,
  standalone: true,
  imports: [
    FuseLoadingBarComponent,
    MatButtonModule,
    MatIconModule,
    MatMenuModule,
    MatFormFieldModule,
    MatInputModule,
    FormsModule,
    RouterLink,
    UserComponent,
    PwaInstallComponent,
    ThemeToggleComponent,
    RouterOutlet,
    FuseVerticalNavigationComponent,
  ],
})
export class CompactLayoutComponent implements OnInit, OnDestroy {
  private readonly appVersionService = inject(AppVersionService);
  private readonly dateUtils = inject(DateUtilsService);
  isScreenSmall: boolean = false;
  isMobileScreen: boolean = false;
  navigation!: Navigation;
  private _unsubscribeAll: Subject<any> = new Subject<any>();
  appVersion = this.appVersionService.getPkgVersion();
  showVersionInfo = false;
  private readonly enterpriseService = inject(EnterpriseService);
  private readonly userService = inject(UserService);
  private readonly authService = inject(AuthService);
  private readonly securityProfileService = inject(SecurityProfileService);
  private readonly shiftsService = inject(ShiftsService);
  private readonly router = inject(Router);
  private readonly enterpriseFeatureConfigService = inject(EnterpriseFeatureConfigService);

  enterpriseName: string = '';
  titleFormat: number = 1;
  enterpriseFilter: string = '';
  isSwitching: boolean = false;
  switchableEnterprises: Partial<Enterprise>[] = [];

  maxEnterpriseLengthDesktop: number = 250;
  maxEnterpriseLengthMobile: number = 20;

  /**
   * Constructor
   */
  constructor(
    private _navigationService: NavigationService,
    private _fuseMediaWatcherService: FuseMediaWatcherService,
    private _fuseNavigationService: FuseNavigationService
  ) {}

  // -----------------------------------------------------------------------------------------------------
  // @ Accessors
  // -----------------------------------------------------------------------------------------------------

  /**
   * Getter for current year
   */
  get currentYear(): number {
    return this.dateUtils.getYear(this.dateUtils.now());
  }

  get formattedEnterpriseName(): string {
    if (!this.enterpriseName) return '';

    const maxLength = this.isMobileScreen ? this.maxEnterpriseLengthMobile : this.maxEnterpriseLengthDesktop;

    if (this.enterpriseName.length > maxLength) {
      return this.enterpriseName.substring(0, maxLength) + '...';
    }

    return this.enterpriseName;
  }

  // -----------------------------------------------------------------------------------------------------
  // @ Lifecycle hooks
  // -----------------------------------------------------------------------------------------------------

  /**
   * On init
   */
  ngOnInit(): void {
    // React to enterprise changes (e.g. after switching)
    this.enterpriseService
      .getEnterpriseObservable()
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe((enterprise) => {
        this.enterpriseName = enterprise?.name || '';
        this.refreshSwitchableEnterprises();
      });

    // Subscribe to navigation data
    this._navigationService.navigation$.pipe(takeUntil(this._unsubscribeAll)).subscribe((navigation) => {
      this.navigation = navigation as unknown as Navigation;
    });

    // Subscribe to media changes
    this._fuseMediaWatcherService.onMediaChange$
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe(({ matchingAliases }) => {
        // Check if the screen is small
        this.isScreenSmall = !matchingAliases.includes('md');
        // Check if it's a mobile screen
        this.isMobileScreen = !matchingAliases.includes('sm');
      });
  }

  /**
   * On destroy
   */
  ngOnDestroy(): void {
    // Unsubscribe from all subscriptions
    this._unsubscribeAll.next(null);
    this._unsubscribeAll.complete();
  }

  // -----------------------------------------------------------------------------------------------------
  // @ Public methods
  // -----------------------------------------------------------------------------------------------------

  /**
   * Toggle navigation
   *
   * @param name
   */
  toggleNavigation(name: string): void {
    // Get the navigation
    const navigation = this._fuseNavigationService.getComponent<FuseVerticalNavigationComponent>(name);

    if (navigation) {
      // Toggle the opened status
      navigation.toggle();
    }
  }

  toggleVersionInfo(): void {
    this.showVersionInfo = !this.showVersionInfo;
  }

  private refreshSwitchableEnterprises(): void {
    const user = this.userService.getUser();
    const currentId = this.enterpriseService.selectedEnterprise?.id;
    const allAvailable = this.enterpriseService.getAvailableEnterprises();

    if (!user || !allAvailable.length) {
      this.switchableEnterprises = [];
      return;
    }

    const isSuperAdmin = user.roles?.some((r) => r.role === ROLES.SUPERADMIN);
    if (isSuperAdmin) {
      this.switchableEnterprises = allAvailable.filter((e) => e.id !== currentId);
      return;
    }

    const nonDismissedIds = new Set((user.roles || []).filter((r) => !r.dismissedAt).map((r) => r.enterprise));
    this.switchableEnterprises = allAvailable.filter((e) => e.id !== currentId && nonDismissedIds.has(e.id!));
  }

  get canSwitchEnterprise(): boolean {
    return this.switchableEnterprises.length > 0;
  }

  get filteredSwitchableEnterprises(): Partial<Enterprise>[] {
    const filter = this.normalizeText(this.enterpriseFilter);
    if (!filter) return this.switchableEnterprises;
    return this.switchableEnterprises.filter((e) => this.normalizeText(e.name || '').includes(filter));
  }

  onSwitchEnterprise(enterprise: Partial<Enterprise>): void {
    if (enterprise.id === this.enterpriseService.selectedEnterprise?.id || this.isSwitching) return;
    this.isSwitching = true;

    this.enterpriseService.fetchEnterpriseById(enterprise.id!).subscribe({
      next: (fullEnterprise) => {
        // Clear all enterprise-specific cached data
        this.userService.clearCache();
        localStorage.removeItem(LOCAL_STORAGE_KEYS.TIME_RECORD);
        localStorage.removeItem(LOCAL_STORAGE_KEYS.SCHEDULE_CONFIGS);
        localStorage.removeItem(LOCAL_STORAGE_KEYS.ASKED_WEEKLY_SCHEDULE);

        // Set new enterprise and resolve role for this enterprise
        this.authService.selectEnterprise(fullEnterprise);

        // Reload the user's main shift to refresh weeklySchedule for the new enterprise
        const user = this.userService.getUser();
        if (user?.id) {
          this.shiftsService.getItems(user.id, fullEnterprise.id).subscribe({
            next: (shifts) => {
              const mainShift = (shifts as any[]).find((s) => s.isMain);
              const updatedUser = this.userService.getUser();
              if (updatedUser) {
                updatedUser.weeklySchedule = mainShift ? mainShift.schedule : undefined;
                this.userService.setUser(updatedUser);
              }
              this.navigateAfterSwitch();
            },
            error: () => this.navigateAfterSwitch(),
          });
        } else {
          this.navigateAfterSwitch();
        }
      },
      error: () => {
        this.isSwitching = false;
      },
    });
  }

  /**
   * A dónde se va uno al cambiar de empresa.
   *
   * ⚠️ **Se recargan los módulos ANTES de decidir.** `authService.selectEnterprise` cambia la
   * empresa y el rol, pero no toca la configuración de módulos: sin este `loadForCurrentEnterprise`
   * el destino se calcularía con los módulos de la empresa que acabamos de dejar.
   *
   * ⚠️ **Y el destino sale de `landingUrlFor`, no de un `if` aquí.** Antes era
   * `ADMIN ? '/enterprise-dashboard' : '/landing/home'` a pelo: al entrar en una empresa sin
   * control horario mandaba al dashboard, que está capado, y `ModuleFeatureGuard` rebotaba al
   * panel. Se acababa en el sitio correcto de casualidad, con un rebote por medio, y a un
   * administrador con «Puede fichar» se le robaba su home.
   *
   * Si falla la carga se navega igual, con lo último que se sepa: dejar al usuario parado en
   * la pantalla de la empresa anterior es peor que un destino imperfecto.
   */
  private navigateAfterSwitch(): void {
    const irA = () => {
      const destino = landingUrlFor(
        this.securityProfileService.getRole(),
        this.enterpriseFeatureConfigService.getSnapshot(),
        canUserTrackTimeInEnterprise(this.userService.getUser(), this.enterpriseService.selectedEnterprise?.id)
      );
      this.router.navigateByUrl(destino).finally(() => {
        this.isSwitching = false;
      });
    };

    this.enterpriseFeatureConfigService.loadForCurrentEnterprise().subscribe({
      next: irA,
      error: irA,
    });
  }

  private normalizeText(value: string): string {
    return value
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
      .trim();
  }
}
